// frontend/src/pages/PaymentCallback.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { FiLoader, FiCheck, FiX, FiAlertCircle } from "react-icons/fi";
import "../styles/payment-callback.css";

const PaymentCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("");
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    const handlePaymentCallback = async () => {
      const params = new URLSearchParams(location.search);
      const paymentId = params.get("payment_id");
      const transactionRef = params.get("transaction_ref");
      const paymentStatus = params.get("payment_status");
      const orderNumber = params.get("order_number");
      const error = params.get("error");
      const errorMessage = params.get("error_message");

      console.log("📨 Payment callback received:", {
        paymentId,
        transactionRef,
        paymentStatus,
        orderNumber,
        error,
        errorMessage,
      });

      if (error) {
        setStatus("failed");
        setMessage(errorMessage || "Payment failed. Please try again.");
        toast.error(errorMessage || "Payment failed");

        // Cancel the payment if needed
        if (paymentId) {
          try {
            await axios.post(
              "/api/payment/cancel",
              {
                paymentId: paymentId,
                reason: errorMessage || "Payment failed",
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                  "x-tenant-code": localStorage.getItem("tenantCode"),
                },
              },
            );
          } catch (err) {
            console.error("Error cancelling payment:", err);
          }
        }
        return;
      }

      if (!paymentId || !paymentStatus) {
        setStatus("error");
        setMessage("Invalid payment callback");
        return;
      }

      try {
        // Confirm the payment
        const response = await axios.post(
          "/api/payment/confirm",
          {
            paymentId: paymentId,
            transactionReference: transactionRef,
            gatewayResponse: {
              status: paymentStatus,
              receivedAt: new Date().toISOString(),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "x-tenant-code": localStorage.getItem("tenantCode"),
            },
          },
        );

        if (response.data.success) {
          setStatus("success");
          setOrderId(response.data.payment.orderId);
          setMessage("Payment successful! Your order has been confirmed.");
          toast.success("Payment successful! 🎉");

          // Clear pending payment data
          localStorage.removeItem("pendingPaymentId");
          localStorage.removeItem("pendingOrderId");

          // Clear cart
          try {
            const { clearCart } = require("../context/CartContext");
            await clearCart();
          } catch (err) {
            console.log("Cart cleared or already empty");
          }

          // Redirect to order confirmation after delay
          setTimeout(() => {
            navigate(`/order-confirmation/${response.data.payment.orderId}`);
          }, 3000);
        } else {
          setStatus("failed");
          setMessage(response.data.message || "Payment verification failed");
          toast.error("Payment verification failed");
        }
      } catch (error) {
        console.error("❌ Payment callback error:", error);

        // Check if it's a duplicate confirmation
        if (error.response?.data?.alreadyCaptured) {
          setStatus("success");
          setMessage("Payment already confirmed. Redirecting...");
          toast.info("Payment already confirmed");

          setTimeout(() => {
            navigate("/orders");
          }, 2000);
        } else {
          setStatus("failed");
          setMessage(
            error.response?.data?.message || "Error processing payment",
          );
          toast.error(
            error.response?.data?.message || "Error processing payment",
          );
        }
      }
    };

    handlePaymentCallback();
  }, [location, navigate]);

  const handleRetry = () => {
    navigate("/checkout");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleViewOrders = () => {
    navigate("/orders");
  };

  return (
    <div className="payment-callback-page">
      <div className="payment-callback-container">
        {status === "processing" && (
          <>
            <div className="payment-callback-icon processing">
              <FiLoader className="spinner" size={64} />
            </div>
            <h2>Processing Your Payment</h2>
            <p>Please wait while we confirm your payment...</p>
            <div className="payment-callback-progress">
              <div className="progress-bar">
                <div className="progress-fill active"></div>
              </div>
            </div>
          </>
        )}

        {status === "success" && (
          <>
            <div className="payment-callback-icon success">
              <FiCheck size={64} />
            </div>
            <h2>Payment Successful! 🎉</h2>
            <p>{message}</p>
            <p className="payment-callback-subtext">
              You will receive a confirmation email shortly.
            </p>
            <div className="payment-callback-actions">
              <button className="btn-primary" onClick={handleViewOrders}>
                View My Orders
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Continue Shopping
              </button>
            </div>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="payment-callback-icon failed">
              <FiX size={64} />
            </div>
            <h2>Payment Failed</h2>
            <p>{message}</p>
            <div className="payment-callback-actions">
              <button className="btn-primary" onClick={handleRetry}>
                Try Again
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Go Home
              </button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="payment-callback-icon error">
              <FiAlertCircle size={64} />
            </div>
            <h2>Something Went Wrong</h2>
            <p>{message}</p>
            <div className="payment-callback-actions">
              <button className="btn-primary" onClick={handleRetry}>
                Try Again
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Go Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
